import { type FunctionalComponent } from '@bpmn-io/properties-panel/preact';
import { useEffect, useMemo, useRef } from '@bpmn-io/properties-panel/preact/hooks';
import { jsx } from '@bpmn-io/properties-panel/preact/jsx-runtime';
import { is } from 'bpmn-js/lib/util/ModelUtil';
import { selfAndAllChildren } from 'diagram-js/lib/util/Elements';
import { createRoot, type Root } from 'react-dom/client';
import { useService } from '../hooks/useService';
import { renderTextWithFieldPicker } from '../../../../pickers/TextWithFieldPicker';
import { getExtensionElementsList } from 'camunda-bpmn-js/lib/util/ExtensionElementsUtil';

interface IMailFieldsProps {
    attrPropName: 'body' | 'subject';
    element: any;
    id: string;
    label: string;
    moddleElement: any;
    multiline?: boolean;
    numberOfLines?: number;
    isMail?: boolean;
    type: string;
    valPropName: string;
}

export const TextWithFieldRef: FunctionalComponent<IMailFieldsProps> = (props) => {
    const { attrPropName, element, label, moddleElement, multiline, type, numberOfLines } = props;
    const bpmnFactory = useService('bpmnFactory');
    const modeling = useService('modeling');
    const translate = useService('translate');
    const re = useService('canvas').getRootElement();
    const rbo = re.businessObject;
    const siteUrl: string | undefined = rbo.siteUrl;
    const list: string | undefined = rbo.listId;
    const containerRef = useRef<HTMLDivElement>();
    const rootRef = useRef<Root>();
    const value = (moddleElement[attrPropName] || {}).value || '';

    useEffect(() => {
        const root = rootRef.current || createRoot(containerRef.current!);
        rootRef.current = root;

        renderTextWithFieldPicker(root, {
            getVariables: () => {
                const vars = selfAndAllChildren([re as any])
                    .map((entry) => {
                        const options: any[] = [];

                        if (is(entry, 'bpmn:ServiceTask')) {
                            let resultVar = entry.businessObject.resultVariable;
                            if (typeof resultVar === 'string') {
                                resultVar = resultVar.trim();
                                if (resultVar !== '') {
                                    options.push({
                                        displayName: resultVar,
                                        internalName: resultVar,
                                    });
                                }
                            }
                        }

                        options.push({
                            displayName: 'Link auf Item',
                            internalName: 'ItemLink',
                        });

                        return options.concat(
                            getExtensionElementsList(entry, 'camunda:InputOutput').map(
                                ({ outputParameters }) => {
                                    if (Array.isArray(outputParameters)) {
                                        return outputParameters.map(({ name }) => {
                                            if (typeof name === 'string') {
                                                name = name.trim();
                                                if (name !== '') {
                                                    return {
                                                        displayName: name,
                                                        internalName: name,
                                                    };
                                                }
                                            }
                                        });
                                    }
                                }
                            )
                        );
                    })
                    .flat(Infinity);

                return Array.from(
                    new Map(vars.map((entry) => [entry.internalName, entry])).values()
                ).sort((a, b) => a.displayName.localeCompare(b.displayName));
            },
            label,
            list,
            multiline,
            numberOfLines,
            onChange: (value) => {
                if (value.length > 0) {
                    // create/override and update
                    modeling.updateModdleProperties(element, moddleElement, {
                        [attrPropName]: bpmnFactory.create(type, {
                            value,
                        }),
                    });
                } else if (moddleElement[attrPropName]) {
                    // remove and update
                    modeling.updateModdleProperties(element, moddleElement, {
                        [attrPropName]: undefined,
                    });
                }
            },
            siteUrl,
            translate,
            value,
        });
    }, [attrPropName, label, multiline, siteUrl, value, numberOfLines]);

    useEffect(
        () => () => {
            if (rootRef.current) {
                rootRef.current.unmount();
            }
        },
        []
    );

    return useMemo(
        () =>
            // @ts-ignore
            jsx('div', {
                className: 'bio-properties-panel-entry',
                // 'data-entry-id': id,
                // id,
                ref: containerRef,
            }),
        []
    );
};
